import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { colors } from '../styles/colors';

interface CompanyFormProps {
  recruiterId: number;
  company: {
    id: number;
    company_id: number;
    company_name: string;
    owner: boolean;
    active: boolean;
    pending: boolean;
    publish_opportunity: boolean;
    candidates_database_access: boolean;
  } | null;
  onSave: () => void;
  onCancel: () => void;
}

const RecruiterCompanyForm: React.FC<CompanyFormProps> = ({ recruiterId, company, onSave, onCancel }) => {
  const isAddMode = !company;
  const [searchType, setSearchType] = useState('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [searching, setSearching] = useState(false);
  const [formData, setFormData] = useState({
    owner: false,
    active: true,
    pending: false,
    publish_opportunity: false,
    candidates_database_access: false,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (company) {
      setFormData({
        owner: company.owner || false,
        active: company.active || false,
        pending: company.pending || false,
        publish_opportunity: company.publish_opportunity || false,
        candidates_database_access: company.candidates_database_access || false,
      });
    }
  }, [company]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (searchType === 'cnpj') {
      const numbers = value.replace(/\D/g, '');
      const formatted = numbers.length <= 14 
        ? numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
        : numbers.slice(0, 14).replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      setSearchTerm(formatted);
    } else {
      setSearchTerm(value);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    setSearching(true);
    try {
      // Remover máscara do CNPJ antes de enviar
      const searchValue = searchType === 'cnpj' ? searchTerm.replace(/\D/g, '') : searchTerm;
      
      const params = new URLSearchParams({
        search: searchValue,
        searchType: searchType
      });
      const response = await api.get(`/companies?${params}`);
      setSearchResults(response.data.companies || []);
      if (response.data.companies.length === 0) {
        toast.info('Nenhuma empresa encontrada');
      }
    } catch (error) {
      toast.error('Erro ao buscar empresa');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectCompany = (company: any) => {
    setSelectedCompany(company);
    setSearchResults([]);
    setSearchTerm('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isAddMode && !selectedCompany) {
      toast.error('Selecione uma empresa');
      return;
    }
    
    setLoading(true);

    try {
      if (isAddMode) {
        await api.post(`/recruiter-list/${recruiterId}/companies`, {
          company_id: selectedCompany.id,
          ...formData
        });
        toast.success('Empresa adicionada com sucesso!');
      } else {
        await api.put(`/recruiter-list/${recruiterId}/companies/${company!.id}`, formData);
        toast.success('Recrutador atualizado com sucesso!');
      }
      onSave();
    } catch (error: any) {
      toast.error(error.response?.data?.error || `Erro ao ${isAddMode ? 'adicionar' : 'atualizar'} empresa`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>{isAddMode ? 'Adicionar Empresa' : 'Editar Empresa'}</h2>
          <button onClick={onCancel} style={styles.closeBtn} type="button">×</button>
        </div>
        
        <form onSubmit={handleSubmit} style={styles.form}>
          {isAddMode ? (
            <>
              <div style={styles.searchSection}>
                <h3 style={styles.sectionTitle}>Buscar Empresa</h3>
                <div style={styles.searchForm}>
                  <select
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value)}
                    style={styles.select}
                  >
                    <option value="name">Nome</option>
                    <option value="cnpj">CNPJ</option>
                    <option value="id">ID</option>
                  </select>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSearch(e as any);
                      }
                    }}
                    placeholder={`Buscar por ${searchType === 'id' ? 'ID' : searchType === 'cnpj' ? 'CNPJ' : 'nome'}...`}
                    style={styles.searchInput}
                  />
                  <button
                    type="button"
                    onClick={handleSearch}
                    disabled={searching}
                    style={styles.searchBtn}
                  >
                    {searching ? 'Buscando...' : 'Buscar'}
                  </button>
                </div>
                
                {searchResults.length > 0 && (
                  <div style={styles.searchResults}>
                    {searchResults.map((comp) => (
                      <div
                        key={comp.id}
                        onClick={() => handleSelectCompany(comp)}
                        style={styles.searchResultItem}
                      >
                        <strong>{comp.name}</strong>
                        <div style={styles.searchResultDetails}>
                          ID: {comp.id} | CNPJ: {comp.cnpj || '-'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {selectedCompany && (
                  <div style={styles.selectedCompany}>
                    <strong>Empresa Selecionada:</strong>
                    <div>{selectedCompany.name}</div>
                    <div style={styles.searchResultDetails}>
                      ID: {selectedCompany.id} | CNPJ: {selectedCompany.cnpj || '-'}
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedCompany(null)}
                      style={styles.removeBtn}
                    >
                      Remover Seleção
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={styles.infoSection}>
              <div style={styles.infoRow}>
                <strong>Empresa:</strong> {company!.company_name}
              </div>
            </div>
          )}

          <h3 style={styles.sectionTitle}>Recrutador</h3>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.owner}
                onChange={(e) => handleChange('owner', e.target.checked)}
              />
              Owner
            </label>
          </div>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => handleChange('active', e.target.checked)}
              />
              Ativo
            </label>
          </div>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.pending}
                onChange={(e) => handleChange('pending', e.target.checked)}
              />
              Pendente
            </label>
          </div>

          <h3 style={styles.sectionTitle}>Permissões</h3>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.publish_opportunity}
                onChange={(e) => handleChange('publish_opportunity', e.target.checked)}
              />
              Publicar Oportunidade
            </label>
          </div>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.candidates_database_access}
                onChange={(e) => handleChange('candidates_database_access', e.target.checked)}
              />
              Base de Candidatos
            </label>
          </div>
          
          <div style={styles.buttons}>
            <button
              type="button"
              onClick={onCancel}
              style={styles.cancelBtn}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || (isAddMode && !selectedCompany)}
              style={{
                ...styles.saveBtn,
                ...(loading || (isAddMode && !selectedCompany) ? styles.disabledBtn : {})
              }}
            >
              {loading ? 'Salvando...' : isAddMode ? 'Adicionar' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1002,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem 2rem 0 2rem',
  },
  title: {
    margin: 0,
    color: colors.primary,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: colors.textLight,
    padding: 0,
    lineHeight: 1,
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    padding: '0 2rem 2rem 2rem',
  },
  infoSection: {
    backgroundColor: '#f5f5f5',
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1.5rem',
    marginTop: '1rem',
  },
  infoRow: {
    marginBottom: '0.5rem',
    color: colors.text,
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: colors.primary,
    marginTop: '1.5rem',
    marginBottom: '1rem',
    borderBottom: `2px solid ${colors.primary}`,
    paddingBottom: '0.5rem',
  },
  checkboxGroup: {
    marginBottom: '1rem',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: colors.text,
    cursor: 'pointer',
    fontSize: '1rem',
  },
  buttons: {
    display: 'flex',
    gap: '1rem',
    marginTop: '1.5rem',
  },
  cancelBtn: {
    flex: 1,
    padding: '0.75rem',
    backgroundColor: colors.border,
    color: colors.text,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  saveBtn: {
    flex: 1,
    padding: '0.75rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  disabledBtn: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  searchSection: {
    marginBottom: '1.5rem',
  },
  searchForm: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  select: {
    padding: '0.5rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '0.9rem',
  },
  searchInput: {
    flex: 1,
    padding: '0.5rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '0.9rem',
  },
  searchBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  searchResults: {
    maxHeight: '200px',
    overflowY: 'auto' as const,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    marginBottom: '1rem',
  },
  searchResultItem: {
    padding: '0.75rem',
    borderBottom: `1px solid ${colors.border}`,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  searchResultDetails: {
    fontSize: '0.85rem',
    color: colors.textLight,
    marginTop: '0.25rem',
  },
  selectedCompany: {
    padding: '1rem',
    backgroundColor: '#e8f5e9',
    border: `1px solid #4caf50`,
    borderRadius: '4px',
  },
  removeBtn: {
    marginTop: '0.5rem',
    padding: '0.25rem 0.5rem',
    backgroundColor: colors.error,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
  },
};

export default RecruiterCompanyForm;
